import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronDown, Search } from 'lucide-react';
import { api } from '../../lib/api.ts';
import { previewGoogle, registrarFuentes } from '../../lib/fuentes.ts';

export interface FuenteEmpresa {
  id: string;
  familia: string;
  peso: number;
  italic: boolean;
  url: string | null;
}

interface FamiliaCatalogo {
  familia: string;
  pesos: number[];
}

/**
 * Selector de tipografía (referencia: un picker, no un select del sistema): buscador,
 * cada familia pintada con su propia letra, y elección de peso. Las familias propias
 * van primero; las de Google se previsualizan con su CSS y, al elegir un peso, se
 * AUTOALOJAN (D8) — el renderer usa el archivo propio, nunca el CDN.
 */
export function SelectorFuente({
  fuentes,
  valor,
  peso,
  onElegir,
  compacto = false,
}: {
  fuentes: FuenteEmpresa[];
  /** fuenteId actual (fila familia+peso). */
  valor: string | null;
  peso?: number;
  onElegir: (fuente: FuenteEmpresa) => void;
  compacto?: boolean;
}) {
  const qc = useQueryClient();
  const [abierto, setAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [familiaActiva, setFamiliaActiva] = useState<string | null>(null);
  const contenedorRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number; arriba: boolean }>({ left: 0, top: 0, arriba: false });
  const ANCHO = 380;
  const ALTO_MAX = 380;

  // El panel tiene overflow: el popover vive en un portal con posición FIJA
  // calculada desde el botón, y se voltea hacia arriba si no cabe abajo.
  useLayoutEffect(() => {
    if (!abierto) return;
    function ubicar() {
      const r = contenedorRef.current?.getBoundingClientRect();
      if (!r) return;
      const left = Math.max(8, Math.min(r.right - ANCHO, window.innerWidth - ANCHO - 8));
      const cabeAbajo = r.bottom + 8 + ALTO_MAX <= window.innerHeight;
      setPos({ left, top: cabeAbajo ? r.bottom + 8 : Math.max(8, r.top - 8 - ALTO_MAX), arriba: !cabeAbajo });
    }
    ubicar();
    window.addEventListener('resize', ubicar);
    window.addEventListener('scroll', ubicar, true);
    return () => {
      window.removeEventListener('resize', ubicar);
      window.removeEventListener('scroll', ubicar, true);
    };
  }, [abierto]);

  const catalogo = useQuery({
    queryKey: ['fuentes-catalogo'],
    queryFn: () => api<{ items: FamiliaCatalogo[] }>('/admin/fuentes/catalogo'),
    staleTime: Infinity,
  });

  const actual = fuentes.find((f) => f.id === valor) ?? null;

  // Familias: propias (con sus pesos reales) + catálogo de Google.
  const familias = useMemo(() => {
    const propias = new Map<string, { familia: string; pesos: number[]; propia: boolean }>();
    for (const f of fuentes) {
      const e = propias.get(f.familia) ?? { familia: f.familia, pesos: [], propia: true };
      if (!e.pesos.includes(f.peso)) e.pesos.push(f.peso);
      propias.set(f.familia, e);
    }
    const lista = [...propias.values()].map((e) => ({ ...e, pesos: e.pesos.sort((a, b) => a - b) }));
    for (const c of catalogo.data?.items ?? []) {
      const existente = propias.get(c.familia);
      if (existente) {
        // pesos adicionales disponibles para autoalojar
        for (const p of c.pesos) if (!existente.pesos.includes(p)) existente.pesos.push(p);
        existente.pesos.sort((a, b) => a - b);
      } else lista.push({ familia: c.familia, pesos: c.pesos, propia: false });
    }
    const q = busqueda.trim().toLowerCase();
    return q ? lista.filter((f) => f.familia.toLowerCase().includes(q)) : lista;
  }, [fuentes, catalogo.data, busqueda]);

  // Preview en su propia letra para las familias visibles.
  useEffect(() => {
    if (!abierto) return;
    for (const f of familias.slice(0, 80)) {
      if (!f.propia) previewGoogle(f.familia, [400]);
    }
  }, [abierto, familias]);

  // Cerrar al hacer clic fuera.
  useEffect(() => {
    if (!abierto) return;
    function fuera(ev: PointerEvent) {
      const objetivo = ev.target as Node;
      if (contenedorRef.current?.contains(objetivo) || popoverRef.current?.contains(objetivo)) return;
      setAbierto(false);
    }
    window.addEventListener('pointerdown', fuera);
    return () => window.removeEventListener('pointerdown', fuera);
  }, [abierto]);

  const autoalojar = useMutation({
    mutationFn: async ({ familia, peso: p }: { familia: string; peso: number }) => {
      await api('/admin/fuentes/google', { metodo: 'POST', cuerpo: { familia, pesos: [p] } });
      const { items } = await api<{ items: FuenteEmpresa[] }>('/admin/fuentes');
      await qc.invalidateQueries({ queryKey: ['admin-fuentes'] });
      const fila = items.find((f) => f.familia === familia && f.peso === p && !f.italic);
      if (!fila) throw new Error('No se pudo registrar la fuente');
      await registrarFuentes([{ fuenteId: fila.id, familia: fila.familia, url: fila.url, peso: fila.peso, italic: fila.italic }]);
      return fila;
    },
  });

  async function elegir(familia: string, p: number) {
    const propia = fuentes.find((f) => f.familia === familia && f.peso === p && !f.italic);
    if (propia) {
      onElegir(propia);
    } else {
      const fila = await autoalojar.mutateAsync({ familia, peso: p });
      onElegir(fila);
    }
    setAbierto(false);
  }

  const familiaMostrada = familiaActiva ?? actual?.familia ?? null;
  const entradaActiva = familias.find((f) => f.familia === familiaMostrada) ?? null;

  return (
    <div ref={contenedorRef} className="relative">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className={`campo flex items-center justify-between gap-2 text-left ${compacto ? 'campo-compacto min-h-9 rounded-xl text-xs' : ''}`}
        style={{ fontFamily: actual ? `'${actual.familia}', sans-serif` : undefined }}
      >
        <span className="truncate">
          {actual ? `${actual.familia} ${peso ?? actual.peso}` : 'Elegir tipografía…'}
        </span>
        <ChevronDown size={14} className="flex-none text-suave" />
      </button>

      {abierto
        ? createPortal(
        <div
          ref={popoverRef}
          className="tarjeta fixed z-[100] overflow-hidden p-2 shadow-[var(--sombra-alta)]"
          style={{ left: pos.left, top: pos.top, width: ANCHO, maxWidth: 'calc(100vw - 16px)' }}
        >
          <div className="relative mb-2">
            <Search size={14} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-suave" />
            <input
              autoFocus
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Buscar tipografía…"
              className="campo campo-compacto con-icono min-h-9 rounded-xl text-xs"
            />
          </div>

          <div className="flex gap-2">
            {/* Familias */}
            <ul className="max-h-72 flex-1 overflow-y-auto">
              {familias.map((f) => (
                <li key={f.familia}>
                  <button
                    type="button"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => setFamiliaActiva(f.familia)}
                    className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-[15px] transition hover:bg-superficie2 ${
                      familiaMostrada === f.familia ? 'bg-superficie2' : ''
                    }`}
                    style={{ fontFamily: `'${f.familia}', sans-serif` }}
                  >
                    <span className="truncate">{f.familia}</span>
                    {f.propia ? <Check size={12} className="flex-none text-suave" /> : null}
                  </button>
                </li>
              ))}
              {familias.length === 0 ? <li className="px-3 py-4 text-xs text-suave">Sin resultados</li> : null}
            </ul>

            {/* Pesos de la familia activa */}
            <div className="w-24 flex-none border-l border-superficie2 pl-2">
              <p className="mb-1 px-1 text-[10px] uppercase tracking-wide text-suave">Peso</p>
              {entradaActiva ? (
                entradaActiva.pesos.map((p) => {
                  const activo = actual?.familia === entradaActiva.familia && (peso ?? actual?.peso) === p;
                  const alojado = fuentes.some((f) => f.familia === entradaActiva.familia && f.peso === p);
                  return (
                    <button
                      key={p}
                      type="button"
                      disabled={autoalojar.isPending}
                      onClick={() => void elegir(entradaActiva.familia, p)}
                      className={`mb-1 flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-xs transition hover:bg-superficie2 ${activo ? 'bg-negro text-white' : ''}`}
                      style={{ fontFamily: `'${entradaActiva.familia}', sans-serif`, fontWeight: p }}
                      title={alojado ? 'Autoalojada' : 'Se descargará y autoalojará'}
                    >
                      {p}
                      {alojado ? <Check size={11} /> : null}
                    </button>
                  );
                })
              ) : (
                <p className="px-1 text-[11px] text-suave">Elija una familia</p>
              )}
              {autoalojar.isPending ? <p className="px-1 text-[10px] text-suave">Descargando…</p> : null}
            </div>
          </div>
        </div>,
        document.body,
      )
        : null}
    </div>
  );
}
